import React, { useState } from "react";
import { Box, Paper, Typography, TextField, IconButton } from "@mui/material";
import {
  ChatBubbleOutlined as MessageIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Remark } from "./types";

interface FurtherRemarksSectionProps {
  remarks: Remark[];
  onAddRemark: (content: string) => void;
  onDeleteRemark: (id: string) => void;
}

const FurtherRemarksSection: React.FC<FurtherRemarksSectionProps> = ({
  remarks,
  onAddRemark,
  onDeleteRemark,
}) => {
  const { t } = useTranslation();
  const [newRemark, setNewRemark] = useState("");

  const handleAdd = () => {
    if (newRemark.trim()) {
      onAddRemark(newRemark.trim());
      setNewRemark("");
    }
  };

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <MessageIcon fontSize="small" />
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            mb: 0,
            textTransform: "uppercase"
          }}>
          {t("coachingNavigator.clientOverview.furtherRemarks")}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          value={newRemark}
          onChange={(e) => setNewRemark(e.target.value)}
          placeholder={t("coachingNavigator.clientOverview.addRemarkPlaceholder")}
          multiline
          rows={2}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <IconButton
          onClick={handleAdd}
          disabled={!newRemark.trim()}
          color="primary"
          sx={{ alignSelf: "stretch", aspectRatio: 1 }}
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Box sx={{ maxHeight: 150, overflow: "auto" }}>
        {remarks.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic"
            }}>
            {t("coachingNavigator.clientOverview.noRemarksYet")}
          </Typography>
        ) : (
          remarks.map((remark) => (
            <Box
              key={remark.id}
              sx={{
                borderLeft: 2,
                borderColor: "divider",
                pl: 1.5,
                py: 1,
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5
                  }}>
                  {dayjs(remark.timestamp).format("MMM D, YYYY • HH:mm")}
                </Typography>
                <Typography variant="body2">{remark.content}</Typography>
              </Box>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDeleteRemark(remark.id)}
                sx={{ ml: 1, flexShrink: 0 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default FurtherRemarksSection;
